/**
 * ChecklistBlock - Interactive checklist with local persistence
 */

import React, { useState, useEffect } from 'react'
import { ChecklistBlock as ChecklistBlockType } from '../../../../lib/cockpit/schemas'

interface ChecklistItem {
  id: string
  text: string
  completed?: boolean
  category?: string
  notes?: string
}

interface ChecklistBlockProps {
  data: ChecklistBlockType['data']
  blockId: string
  compact?: boolean
}

function ChecklistBlock({ data, blockId, compact }: ChecklistBlockProps) {
  // Local state for checked items (with optional persistence)
  const [checkedItems, setCheckedItems] = useState<Set<string>>(() => {
    // Try to load from localStorage
    if (typeof window !== 'undefined' && data.allow_user_edits) {
      try {
        const saved = localStorage.getItem(`checklist-${blockId}`)
        if (saved) {
          return new Set(JSON.parse(saved))
        }
      } catch {}
    }
    // Initialize from data
    return new Set(data.items.filter((i: ChecklistItem) => i.completed).map((i: ChecklistItem) => i.id))
  })

  // Save to localStorage when changed
  useEffect(() => {
    if (typeof window !== 'undefined' && data.allow_user_edits) {
      localStorage.setItem(`checklist-${blockId}`, JSON.stringify([...checkedItems]))
    }
  }, [checkedItems, blockId, data.allow_user_edits])

  const toggleItem = (itemId: string) => {
    if (!data.allow_user_edits) return
    
    setCheckedItems(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  const completedCount = data.items.filter((i: ChecklistItem) => checkedItems.has(i.id)).length
  const progress = (completedCount / data.items.length) * 100

  // Group by category if categories exist
  const groupedItems: Array<{ category: string | null; items: ChecklistItem[] }> = data.categories
    ? data.categories.map((category: string) => ({
        category,
        items: data.items.filter((i: ChecklistItem) => i.category === category),
      }))
    : [{ category: null, items: data.items }]

  if (compact) {
    return (
      <div className="space-y-2">
        {/* Progress bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {completedCount}/{data.items.length}
          </span>
        </div>
        
        {/* First few unchecked items */}
        {data.items.filter((i: ChecklistItem) => !checkedItems.has(i.id)).slice(0, 3).map((item: ChecklistItem) => (
          <div key={item.id} className="text-sm text-gray-700 dark:text-gray-300 truncate">
            □ {item.text}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {completedCount} of {data.items.length}
        </span>
      </div>

      {/* Items */}
      {groupedItems.map(({ category, items }: { category: string | null; items: ChecklistItem[] }, groupIndex: number) => (
        <div key={category || groupIndex}>
          {category && (
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
              {category}
            </h4>
          )}
          <div className="space-y-2">
            {items.map((item: ChecklistItem) => {
              const isChecked = checkedItems.has(item.id)
              
              return (
                <div
                  key={item.id}
                  className={`
                    flex items-start gap-3 p-3 rounded-lg border transition-colors
                    ${isChecked 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                    ${data.allow_user_edits ? 'cursor-pointer' : ''}
                  `}
                  onClick={() => toggleItem(item.id)}
                >
                  <div className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                    ${isChecked 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-gray-300 dark:border-gray-600'
                    }
                  `}>
                    {isChecked && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`text-sm ${isChecked ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                      {item.text}
                    </div>
                    {item.notes && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                        {item.notes}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default ChecklistBlock



